import React from 'react';
import { useDispatch } from 'react-redux';
import Lottie from 'react-lottie';
import { Link } from 'react-router-dom';
import { Image } from 'react-bootstrap';

import CustomRichTextViewer from 'components/base/CustomRichTextViewer';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import 'containers/customers/customer/CustomerOverviewDetail.scss';

import { addDomainToImage, getImageUrl, isJsonFile } from 'utils';
import LineItemGroupsDetail from 'components/earning/LineItemGroupsDetail';

import EditIcon from '../../assets/images/campaignStreamline/edit_icon.png';
import TitleError from '../../assets/images/campaignStreamline/title_error.png';
import './SinglePreviewCard.scss';
import i18n from 'i18n-js';
import { isEmpty } from 'utils/StringUtil';

export const TitleWithEditButton = ({
  itemTitle,
  showEditButton = false,
  errors = [],
  errorField = null,
  isMandatory,
  dispatch = () => { },
  step = null,
  anchorKey = null,
  sectionKey = null,
}) => (
  <div className='single-preview-card-edit-title'>
    {
      errors.includes(errorField) && (
        <Image src={TitleError} className="single-preview-card-edit-title-error" />
      )
    }
    <label
      id={anchorKey}
      className={`create-section-label create-section-label-bottom-space`}
    >
      {itemTitle}
    </label>
    {
      showEditButton && (
        <>
          <span className={`single-preview-card-title-suffix ${isMandatory ? 'single-preview-card-title-suffix-mandatory' : ''}`} >
            {isMandatory ? "*" : "(optional)"}
          </span>
          <div
            className='single-preview-card-edit-container'
            onClick={() => {
              dispatch({
                type: 'campaignStreamline/stepChange',
                payload: { step, anchorKey, sectionKey }
              });
            }}
          >
            <Image src={EditIcon} className="single-preview-card-edit-icon" />
            <span className='single-preview-card-edit'>Edit</span>
          </div>
        </>
      )
    }
  </div>
);


const getNestedValue = (obj, path) => {
  const keys = path.split('.');
  let result = obj;

  for (const key of keys) {
    if (!result) {
      return null;
    }
    result = result?.[key];
  }

  return result || null;
};

const getDisplayValue = (data, column, language) => {
  let dataDict = data;
  if (column.haveTranslation) {
    if (column.translationPrefix) {
      dataDict = data?.[column.translationPrefix]?.translations?.[language];
    } else {
      dataDict = data?.translations?.[language];
    }
  };

  let value = null;
  if (column.field.includes(".")) {
    value = getNestedValue(dataDict, column.field);
  } else {
    value = dataDict?.[column.field];
  }

  return value;
};

const getDisplayArrayValue = (data, column, language) => {
  let value = getDisplayValue(data, column, language);

  if (Array.isArray(value)) {
    value = value.join('\r\n');
  }

  return value || '-';
};

const singleImage = (item) => {
  const imageValue = getImageUrl(item);
  if (!imageValue) {
    return "-";
  };

  return (
    <>
      {
        isJsonFile(imageValue) ?
          <div
            style={{
              maxWidth: 191,
              maxHeight: 191,
              marginRight: '20px',
              marginBottom: '20px',
            }}
          >
            <Lottie
              alt="img"
              options={{
                loop: true,
                path: addDomainToImage(imageValue),
              }}
            />
          </div>
          :
          <img
            alt="img"
            src={addDomainToImage(imageValue)}
            style={{
              maxWidth: 191,
              maxHeight: 191,
              marginRight: '20px',
              marginBottom: '20px',
            }}
          />
      }
    </>
  )
}

const getDisplayImage = (data, column, language) => {
  const value = getDisplayValue(data, column, language);

  if (Array.isArray(value)) {
    if (!value.length) {
      return '-';
    };
    return (
      <>
        {value.map((item) => singleImage(item))}
      </>
    );
  } else {
    if (!value) {
      return '-';
    };
    return singleImage(value)
  }
};

const getColumnValue = (data, column, language) => {
  if (column.isImage) {
    return getDisplayImage(data, column, language);
  } else {
    return getDisplayArrayValue(data, column, language);
  }
};


// column: { customRender, isRichText, deeplink, isBoolean, isImage }
const renderValueSection = (data, column, language) => {
  if (column?.customRender) {
    return column.customRender;
  }

  const columnValue = getColumnValue(data, column, language);

  if (column.isRichText) {
    return <CustomRichTextViewer initialValue={columnValue} />;
  }
  if (column.deeplink) {
    return (
      <Link
        to={column.deeplink}
        className="create-section-field-input list-new-line"
      >
        {columnValue}
      </Link>
    );
  }
  if (column.isBoolean) {
    return (
      <label className="create-section-field-input list-new-line">
        {columnValue === true || columnValue === 1
          ? 'Yes'
          : 'No'}
      </label>
    );
  }
  return (
    <label className="create-section-field-input list-new-line">
      {columnValue}
    </label>
  );
};


const SingleSection = ({
  language,
  section,
  data,
  isFirst,
  errors = [],
  hideEditButton=false,
}) => {
  const dispatch = useDispatch();

  return (
    <>
      {!isFirst && <div className='single-preview-card-split-line' />}
      <div id={section.anchorKey} />
      {section?.sectionTitle &&
        <label
          className="create-section-title single-preview-card-section-title"
        >
          {section.sectionTitle}
        </label>
      }
      {
        section.sectionContent?.map(item => {
          if (item.hide) {
            return null;
          };

          const itemTitle = item.haveTranslation
            ? i18n.t(item.title, { locale: language })
            : item.title;

          let errorField = item.field;
          if (item.errorField) {
            errorField = item.errorField;
          } else if (item.haveTranslation) {
            if (item.translationPrefix) {
              errorField = `${item.translationPrefix}.translations.${language}.${item.field}`;
            } else {
              errorField = `translations.${language}.${item.field}`;
            };
          };

          // in EarningRulePreviewCard.js
          if (item.field === "linkedEarningRules.lineItemGroups") {
            return <tr>
              <LineItemGroupsDetail data={data?.linkedEarningRules} />
            </tr>
          }
          // in StampCampaignPreviewPropertiesCard.js
          if (item.field === "lineItemGroups") {
            return <tr>
              <LineItemGroupsDetail data={data} />
            </tr>
          }
          return (
            <tr>
              <td
                style={{
                  display: 'inline-grid',
                  width: '100%',
                  wordBreak: `break-word`,
                }}
              >
                {itemTitle === undefined ? null :
                  <TitleWithEditButton
                    itemTitle={itemTitle}
                    showEditButton={!hideEditButton}
                    errors={errors}
                    errorField={errorField}
                    isMandatory={item.isMandatory}
                    dispatch={dispatch}
                    step={isEmpty(item.step) ? section.step : item.step}
                    anchorKey={item.anchorKey}
                    sectionKey={section.anchorKey}
                  />
                }
                {renderValueSection(data, item, language)}
              </td>
            </tr>
          )
        }
        )
      }
    </>
  )
}

const SinglePreviewCard = ({
  language,
  data,
  cardInfo,
  errors,
  hideEditButton = false,
}) => {
  return (
    <>
      <table style={{ tableLayout: 'fixed', width: '100%' }}>
        {cardInfo.map((section, index) => {
          if (section.hide) {
            return null;
          };

          return (
            <SingleSection
              language={language}
              section={section}
              data={data}
              isFirst={index === 0}
              errors={errors}
              hideEditButton={hideEditButton}
            />
          );
        })}
      </table>
    </>
  );
}

export default SinglePreviewCard;
